import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import type { AssetAssignment } from '../api';
import { useDevolver, useLocations } from '../hooks';
import { Button } from '../../../shared/components/Button';
import { Alert } from '../../../shared/components/Alert';
import { TextInput } from '../../../shared/components/TextInput';
import { Textarea } from '../../../shared/components/Textarea';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';

interface Props {
  open: boolean;
  assetId: string;
  assignmentId: string | null;
  onClose: () => void;
  onSuccess: (assignment: AssetAssignment) => void;
}

export function DevolucaoModal({ open, assetId, assignmentId, onClose, onSuccess }: Props) {
  const mutation = useDevolver(assetId);
  const locationsQuery = useLocations();
  const [returnedAt, setReturnedAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [returnReason, setReturnReason] = useState('');
  const [locationId, setLocationId] = useState('');
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setReturnedAt(new Date().toISOString().slice(0, 10));
    setReturnReason(''); setLocationId(''); setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!assignmentId) { setError('Nenhum vínculo ativo para devolução'); return; }
    if (returnReason.trim().length < 3) { setError('Motivo deve ter ao menos 3 caracteres'); return; }
    try {
      const result = await mutation.mutateAsync({
        assignmentId,
        payload: {
          returnedAt: new Date(returnedAt).toISOString(),
          returnReason: returnReason.trim(),
          locationId: locationId || undefined,
        },
      });
      reset();
      onSuccess(result);
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } }).response?.data?.message;
      setError(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Falha ao registrar devolução'));
    }
  }

  return (
    <Dialog open={open} onClose={() => { reset(); onClose(); }} className="relative z-40">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
          <Dialog.Title className="text-lg font-semibold text-gray-900">Registrar devolução</Dialog.Title>
          <form onSubmit={handleSubmit} className="mt-4 space-y-3">
            <TextInput
              label="Data da devolução *"
              type="date"
              value={returnedAt}
              onChange={(e) => setReturnedAt(e.target.value)}
              required
            />
            <Textarea
              label="Motivo *"
              rows={3}
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              required
              minLength={3}
            />
            <AutocompleteSelect
              label="Localização de destino (opcional)"
              options={[
                { value: '', label: 'Sem alteração' },
                ...(locationsQuery.data?.data ?? []).map((l) => ({ value: l.id, label: l.name })),
              ]}
              value={locationId}
              onChange={setLocationId}
            />

            {error && <Alert variant="error">{error}</Alert>}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => { reset(); onClose(); }}>Cancelar</Button>
              <Button type="submit" loading={mutation.isPending}>
                {mutation.isPending ? 'Registrando…' : 'Registrar devolução'}
              </Button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
